import React, { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { IAppState } from '../../../core/store/store'
import {
  Button,
  ButtonType,
  Form,
  FormFieldSize,
  FormGroup,
  Input,
  InputType,
  IToastNotification,
  MultiSelect,
  Select,
  SubmitButton,
  ToasterMessageType
} from '@digicert/dcone-common-ui'
import styles from '../create-user-with-role/CreateUserWithRole.module.scss'
import { Translate } from 'react-localize-redux'
import { mapOptionsCommonUi, translate, } from '../../../shared/helpers/utils'
import { getPermissions } from '../../../core/selectors/Profile.selectors'
import { EMAIL_REGEXP, NAME_REGEXP, RouterConfig, USER_NAME_REGEXP } from '../../../core/utils/constants'
import { IAccessScope, IApplication } from '../../../reducers/Reducers.interfaces'
import history from 'core/history/history'
import { editUser, fetchGroups, fetchUserById } from '../../../core/actions/Users.actions'
import { IOptions, TEditUserWithRolesProps } from './EditUserWithRole.types'
import { checkIfUsernameExists } from 'public-pages/self-service-user/SelfServiceUser.actions'
import { withPermissionsHOC } from 'core/hoc/permissionsHoc'
import NoPermissionsPage from 'shared/components/no-permissions-page'
import { fetchTenantById } from 'secure-pages/tenants/Tenants.actions'
import { CERT_CENTRAL_SHORT_APP_CODE } from 'core/constants'

const EditUserWithRole = (
  {
    checkIfUsernameExists,
    fetchUserById,
    fetchTenantById,
    editUser,
    permissions,
    match,
    localeList,
    fetchGroups,
    profile
  }: TEditUserWithRolesProps): ReactElement => {
  const [currentUser, setCurrentUser] = useState<any>({})
  const [accounts, setAccounts] = useState<any>([])
  const [loaded, setLoaded] = useState(false)
  const [userAccessError, setUserAccessError] = useState(false)
  const [systemUserChecked, setSystemUserChecked] = useState(false);
  const [accountUserChecked, setAccountUserChecked] = useState(false);
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [userName, setUserName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [otherPhoneNumber, setOtherPhoneNumber] = useState('')
  const [language, setLanguage] = useState(translate('common.form.select') as string)
  const [featureIds, setFeatureIds] = useState<string[]>([])
  const [notifications, setNotifications] = useState<IToastNotification[]>([])
  const [userNameExists, setUserNameExists] = useState(false)
  const [disableAccountValidation, setDisableAccountValidation] = useState(false)
  const [groups, setGroups] = useState<string[]>([])
  const [options, setOptions] = useState([])
  const [applications, setApplications] = useState<string[]>([])
  const [certCentralSpAccounts, setCertCentralSpAccounts] = useState<IOptions>()
  const [selectedSpCertCentralSpAccounts, setSelectedSpCertCentralSpAccounts] = useState<string[]>()
  const [certCentralId, setCertCentralId] = useState<string>('')
  const [samlUserName, setSamlUserName] = useState('')
  const [samlNameExists, setSamlNameExists] = useState(false)
  const [oidcUserName, setOidcUserName] = useState('')
  const [oidcNameExists, setOidcNameExists] = useState(false)
  const [enabledServices, setEnabledServices] = useState<IApplication[]>([])

  useEffect(() => {
    if (Object.keys(currentUser).length === 0) {
      fetchUserById(match.params.id).then((response) => {
        const applications = Object.keys(response.applications)
        const apps: string [] = []
        applications.forEach(
          (key) => {
            apps.push(response.applications[key].id)
          })
        setApplications(apps)
        setCurrentUser(response)
        setFirstName(response.first_name)
        setLastName(response.last_name)
        setEmail(response.email)
        setUserName(response.user_name)
        setLanguage(response.locale)
        setGroups(response.groups.map(group => {return group.id}))
        setAccountUserChecked(response.access_scope === IAccessScope.account)
        setSystemUserChecked(response.access_scope === IAccessScope.system)
        setSamlUserName(response.saml_username)
        setOidcUserName(response.oidc_username)
        fetchTenantById(response.primary_account_id).then((acc) => {
          setEnabledServices(acc?.applications)
          acc.applications.map(application => {
            if (application.short_app_code === CERT_CENTRAL_SHORT_APP_CODE && application.service_provider_accounts) {
              const spOptions = application.service_provider_accounts.map(acc => {
                return {
                  value: acc.id,
                  label: acc.source_account_name
                }
              })
              setCertCentralSpAccounts(spOptions)
            }
          })
          applications.forEach(
            (key) => {
              const app = response.applications[key]
            if (app.short_app_code === CERT_CENTRAL_SHORT_APP_CODE) {
              setCertCentralId(app.id)
              const selectedCCAccounts: Array<string> = []
              app.service_provider_accounts?.map(acc => {
                selectedCCAccounts.push(acc.id)
              })
              setSelectedSpCertCentralSpAccounts(selectedCCAccounts)
              }
            })
        })
        const accessibleAccounts: any[] = [];
        for (let i = 0; i < response.accounts.length; i++) {
          accessibleAccounts.push(response.accounts[i].id)
        }
        setAccounts(accessibleAccounts)

        if (response.phone) {
          setPhoneNumber(response.phone)
        }
        if (response.other_phone) {
          setOtherPhoneNumber(response.other_phone)
        }
        setLoaded(true)
      })
      .catch((error: any) => {
        if (error.message.includes('userAccessError')) {
          setUserAccessError(true)
        }
      })
    }
  }, [currentUser, fetchUserById, match.params.id])

  const getAccounts = () => {
    const options = profile?.accounts.filter(account => account.active === true).map((account) => {
      const hasPermissionToAccount = profile?.accounts.some(item => item.id === account.id);
      return {
        label: account.display_name,
        value: account.id,
        locale: account.locale,
        disabled: account.id === currentUser.primary_account_id || !hasPermissionToAccount,
        featureIds: account.enabled_feature_ids
      }
    })
    options?.sort((a, b) => (a.label > b.label ? 1 : -1))
    return options
  }

  const getApplications = () => {
    const applications: Array<any> = []
    const apps = [...enabledServices] || []
    if (apps) {
      Object.keys(apps).reduce(
        (val, key) => {
          const application = {
            label: apps[key].name,
            value: apps[key].id,
            short_app_code: apps[key].short_app_code
          }
          applications.push(application)
          return application
        },
        {}
      )
    }

    applications?.sort((a, b) => (a.label > b.label ? 1 : -1))
    return applications
  }

  const clearAccounts = () => {
    const tmpArray: any = []
    setDisableAccountValidation(true)
    setTimeout(() => {
      getAccounts()?.map((item) => {
        if (item.value === currentUser.primary_account_id && item.disabled) {
          tmpArray.push(item.value)
          setAccounts(tmpArray)
          setDisableAccountValidation(false)
        }
      })
    })
  }

  const onAccountChange = (value) => {
    setAccounts(value)
    if (!systemUserChecked) {
      let array:any = featureIds
      value.forEach(element => {
        const selectedAccount = getAccounts()?.find(item => item.value === element)
        if (selectedAccount) {
          array = array.concat(selectedAccount.featureIds)
          setLanguage(selectedAccount.locale)
        }
      });
      setFeatureIds(array)
    }
    else {
      setLanguage(translate('common.form.select') as string)
    }
  }

  const validateAccountSelect = (): any => {
    if(!disableAccountValidation) {
      return accounts.length === 0 ? translate('common.form.validation.required') : ''
    }
  }

  const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value)
  }

  const onLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value)
  }

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const onUserNameFocus = () => {
    if (userName === '') {
      setUserName(email)
      checkIfUsernameExists(email, undefined, 'userName', currentUser.id).then((response:boolean) => {
        setUserNameExists(response)
      })
    }
    else {
      setUserName(userName)
    }
  }

  const validateUserName = (): any => {
    if (userNameExists) {
      return translate('notifications.exceptions.USERNAME_EXISTS');
    }
  }

  const onUserNameChange = useCallback(
    (event) => {
      setUserName(event.target.value)
      checkIfUsernameExists(event.target.value, undefined, 'userName', currentUser.id).then((response:boolean) => {
        setUserNameExists(response)
      })
    },
    [checkIfUsernameExists]
  )

  const onPhoneChange = (value) => {
    value ? setPhoneNumber(value) : setPhoneNumber('')
  }

  const onOtherPhoneChange = (event) => {
    setOtherPhoneNumber(event.target.value)
  }

  const handleSpMultiSelect = (value): void => {
    setSelectedSpCertCentralSpAccounts(value)
  }


  const onLangChange = (lang: any): void => {
    setLanguage(lang)
  }

  const validateLangSelect = (): any => !language ? translate('common.form.validation.required') : ''

  const onCancel = (e) => {
    e.preventDefault()
    history.goBack()
  }

  const submit = (e) => {
    e.preventDefault()
    const formData : any = {
      "access_scope": systemUserChecked,
      "first_name": firstName,
      "last_name": lastName,
      "email": email,
      "user_name": userName,
      "locale": language,
      "phone": phoneNumber,
      "other_phone": otherPhoneNumber,
      "saml_username": samlUserName,
      "oidc_username": oidcUserName,
      "group_ids": groups
    }
    if (certCentralId && selectedSpCertCentralSpAccounts && selectedSpCertCentralSpAccounts.length > 0) {
      formData.service_provider_account_ids = {[certCentralId]: selectedSpCertCentralSpAccounts}
    }
    if (!systemUserChecked) {
      formData.accounts = accounts
      formData.application_ids = applications
    }
    editUser(match.params.id, formData, false).then(() => {
      history.push(RouterConfig.administrators.details(match.params.id))
      setNotifications(
        [
          ...notifications,
          {
            type: ToasterMessageType.SUCCESS,
            description: translate('notifications.users.editUserSuccess'),
            id: 'user-added'
          }
        ]
      )
    })
  }

  const onSamlUserNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSamlUserName(event.target.value)
    checkIfUsernameExists(event.target.value, undefined, 'ssoUserName', match.params.id).then((response:boolean) => {
      setSamlNameExists(response)
    })
  }

  const validateSamlUserName = (): any => {
    if (samlNameExists) {
      return translate('notifications.exceptions.USERNAME_EXISTS');
    }
  }

  useEffect(() => {
    if (accountUserChecked) {
      fetchGroups(IAccessScope.account.toUpperCase()).then(res => {
        const groupOptions = res.map(group => {
          return { value: group.id, label: translate(`groups.${group.name}`) }
        })
        setOptions(groupOptions)
      })
    } else if (systemUserChecked) {
      fetchGroups(IAccessScope.system.toUpperCase()).then(res => {
        const groupOptions = res.map(group => {
          return { value: group.id, label: translate(`groups.${group.name}`) }
        })
        setOptions(groupOptions)
      })
    }
  }, [accountUserChecked, systemUserChecked, fetchGroups])

  const handleMultiSelect = (value): void => {
    setGroups(value)
  }

  const validateMultiSelect = (): any => {
    if (groups.length === 0) {
      return <Translate id='common.form.validation.required' />
    }
  }

  const handleApplicationMultiSelect = (value): void => {
    let isCertCentralSelected = false
    value.map(v => {
      const application = getApplications().find(app => app.value === v)
      if (application.short_app_code === CERT_CENTRAL_SHORT_APP_CODE) {
        setCertCentralId(application.value)
        isCertCentralSelected = true
      }
    })
    if (!isCertCentralSelected) {
      setCertCentralId('')
      setSelectedSpCertCentralSpAccounts([])
    }
    setApplications(value)
  }

  const validateApplicationMultiSelect = (): any => {
    if (applications.length === 0) {
      setCertCentralId('')
      return <Translate id='common.form.validation.selectServices' />
    }
  }

  const onOidcUserNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setOidcUserName(event.target.value)
    checkIfUsernameExists(event.target.value, undefined, 'oidcUserName', match.params.id).then((response:boolean) => {
      setOidcNameExists(response)
    })
  }

  const validateOidcUserName = (): any => {
    if (oidcNameExists) {
      return translate('notifications.exceptions.USERNAME_EXISTS');
    }
  }

  if (permissions.canManageAccountUser
    ? false
    : !permissions.canManageSystemUser) {
    return <NoPermissionsPage />
  }

  if (currentUser && currentUser.status !== 'ACTIVE' || userAccessError) {
    if (loaded) {
      return (
        <NoPermissionsPage />
      )
    }
    else return <></>
  }

  return (
    <section className={styles.createUser}>
          <h1>{translate('manageUser.administrators.form.editAdmin')}</h1>

          <Form className={styles.form} center={false} onSubmit={(e) => {
            submit(e)
          }}>
                        {((currentUser.access_scope === IAccessScope.system && !systemUserChecked) || (currentUser.access_scope === IAccessScope.account && !systemUserChecked)) && (
              <div className={styles.accessGroup}>
                <MultiSelect
                  getPopupContainer={(triggerNode) => triggerNode.parentElement}
                  help=''
                  label={translate('manageUser.administrators.form.customer') as string}
                  placeholder={translate('common.form.select')}
                  value={accounts}
                  defaultValue={accounts}
                  onChange={onAccountChange}
                  allowClear
                  size={FormFieldSize.XXL}
                  options={getAccounts()}
                  validate={validateAccountSelect}
                  errors={{ valueMissing: translate('common.form.validation.required') }}
                  tooltipProps={{
                    title: translate('common.form.validation.signInAccountNoAccess')
                  }}
                  onClear={clearAccounts}
                  disabled
                />
              </div>
            )}
            <FormGroup><h4><Translate id='common.personalInformation'/></h4></FormGroup>

            <FormGroup>
              <div className={styles.nameGroup}>
                <Input
                  label={translate('common.form.firstName')}
                  required
                  minLength={1}
                  maxLength={256}
                  value={firstName}
                  onChange={onFirstNameChange}
                  pattern={NAME_REGEXP}
                  errors={{
                    valueMissing: translate('common.form.validation.required')
                  }}
                  id='first_name'
                />

                <Input
                  label={translate('common.form.lnOptional')}
                  maxLength={256}
                  value={lastName}
                  onChange={onLastNameChange}
                  id='last_name'
                />
              </div>
            </FormGroup>

            <FormGroup>
              <Input
                size={FormFieldSize.L}
                label={<Translate id='common.form.emailAddress' />}
                required
                value={email}
                onChange={onEmailChange}
                pattern={EMAIL_REGEXP}
                errors={{
                  typeMismatch: translate('notifications.errors.invalidEmailError'),
                  valueMissing: translate('common.form.validation.required'),
                  patternMismatch: translate('notifications.errors.invalidEmailError')
                }}
              />
            </FormGroup>

            <FormGroup>
              <Input
                size={FormFieldSize.L}
                label={<Translate id='common.form.username' />}
                required
                value={userName}
                pattern={USER_NAME_REGEXP}
                errors={{
                  valueMissing: translate('common.form.validation.requiredUserName'),
                  patternMismatch: translate('common.form.validation.invalidUserName')
                }}
                onChange={onUserNameChange}
                onFocus={onUserNameFocus}
                validate={validateUserName}
              />
            </FormGroup>


            {currentUser.sign_in_method.signInMethod === 'singleSignOnSaml' && currentUser.sign_in_method.status === 'enabled' && (
              <FormGroup>
                <Input
                  size={FormFieldSize.L}
                  label={<Translate id='common.form.samlUsername' />}
                  required
                  value={samlUserName}
                  validate={validateSamlUserName}
                  onChange={onSamlUserNameChange}
                  pattern={USER_NAME_REGEXP}
                  disabled={profile?.access_scope !== IAccessScope.system}
                  errors={{
                    valueMissing: translate('common.form.validation.required'),
                    patternMismatch: translate('common.form.validation.invalidUserName')
                  }}
                  />
              </FormGroup>
              )}
            {currentUser.sign_in_method.signInMethod === 'singleSignOnOidc' && currentUser.sign_in_method.status === 'enabled' && (
              <FormGroup>
                <Input
                  size={FormFieldSize.L}
                  label={<Translate id='common.form.oidcUsername' />}
                  required
                  value={oidcUserName}
                  pattern={USER_NAME_REGEXP}
                  disabled={profile?.access_scope !== IAccessScope.system}
                  errors={{
                    valueMissing: translate('common.form.validation.required'),
                    patternMismatch: translate('common.form.validation.invalidUserName')
                  }}
                  onChange={onOidcUserNameChange}
                  validate={validateOidcUserName}
                />
              </FormGroup>
            )}

            <FormGroup>
              <Input
                size={FormFieldSize.L}
                label={translate('common.form.phoneOptional')}
                type={InputType.TELEPHONE}
                validateChanges
                value={phoneNumber}
                errors={{ valueMissing: translate('common.form.validation.required') }}
                onChange={onPhoneChange}
              />
            </FormGroup>

            <FormGroup>
            <Input
              size={FormFieldSize.L}
              label={<Translate id='common.form.otherPhoneOptional'/>}
              value={otherPhoneNumber}
              onChange={onOtherPhoneChange}
            />
          </FormGroup>


            <FormGroup>
              <Select
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                size={FormFieldSize.L}
                allowClear
                defaultValue={language}
                value={language}
                label={translate('profile.defaultLanguage')}
                onChange={onLangChange}
                options={mapOptionsCommonUi(localeList?.locales)}
                errors={{ valueMissing: translate('common.form.validation.required') }}
                validate={validateLangSelect}
                optionFilterProp="label"
                showSearch
              />
            </FormGroup>

        <div className={styles.systemAccountUserInfo}>
          <h4 className={styles.headline}><Translate id='manageUser.administrators.form.accountAccess' /></h4>
          {systemUserChecked && (<p className={styles.description}><Translate id='manageUser.administrators.form.digicertEmployee' /></p>
          )}
        </div>


            {accountUserChecked &&
              <FormGroup>
                <MultiSelect
                  help=""
                  value={applications}
                  placeholder={translate('common.form.select') as string}
                  onChange={handleApplicationMultiSelect}
                  allowClear={true}
                  label={translate('manageUser.administrators.enabledServices') as string}
                  size={FormFieldSize.L}
                  options={getApplications()}
                  validate={validateApplicationMultiSelect}
                />
              </FormGroup>
            }

            {(systemUserChecked || accountUserChecked) && options.length > 0 &&

              <FormGroup>
                <MultiSelect
                  help=""
                  value={groups}
                  placeholder={translate('common.form.select') as string}
                  onChange={handleMultiSelect}
                  allowClear={true}
                  label={translate('manageUser.administrators.groups') as string}
                  size={FormFieldSize.L}
                  options={options}
                  validate={validateMultiSelect}
                />
              </FormGroup>
            }

          {certCentralSpAccounts && certCentralId !== '' &&
            <FormGroup>
              <MultiSelect
                help=""
                value={selectedSpCertCentralSpAccounts}
                placeholder={translate('common.form.select') as string}
                onChange={handleSpMultiSelect}
                allowClear={true}
                label={translate('manageUser.administrators.certCentralAccount') as string}
                size={FormFieldSize.L}
                options={certCentralSpAccounts}
              />
            </FormGroup>
          }


            <div className={styles.formActions}>
              <Button buttonType={ButtonType.SECONDARY} onClick={onCancel}>
                <Translate id='common.form.buttons.cancelBtn' />
              </Button>

              <SubmitButton disabled={!systemUserChecked && accounts.length === 0}>
              <Translate id='common.form.buttons.updateBtn' />
              </SubmitButton>
            </div>
          </Form>
        </section>
  )
}

export default connect((state: IAppState) => ({
    permissions: getPermissions(state),
    profile: state.profile.data,
    localeList: state.locales,
    profileAccessScope: state.profile.data?.access_scope,
    userApplications: state.userApplications.data
  }),
  { fetchUserById, checkIfUsernameExists, editUser, fetchGroups, fetchTenantById }
)(withPermissionsHOC(EditUserWithRole, ['canViewUser']))
